import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { money, errMsg } from "@/api";
import { MODE_LABELS, STATUS_LABELS } from "@/components/Badge";

export default function Reports() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  if (error)
    return (
      <div className="brut p-6" data-testid="reports-error">
        <p className="overline">Raporlar yüklenemedi</p>
        <p className="mt-2 text-sm text-red-700">{error}</p>
      </div>
    );
  if (!data) return <p className="text-sm text-zinc-500" data-testid="reports-loading">Yükleniyor...</p>;
  const t = data.totals;
  const kg = (v) => `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(Number(v || 0))} kg`;
  const num = (v) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(Number(v || 0));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <p className="overline">Finans</p>
        <h1 className="font-display text-4xl font-black tracking-tighter">Raporlar</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {[["Toplam Maliyet", money(t.cost)], ["Toplam Gelir", money(t.sale)], ["Toplam Kâr", money(t.profit)],
          ["Marj", `${t.margin_pct}%`], ["Toplam Tonaj", kg(t.weight_kg)], ["Toplam Gecikme", `${t.delay_hours} saat`],
          ["Denizyolu TEU", num(t.teu)], ["Karayolu Araç Adedi", num(t.vehicle_count)],
          ["Havayolu Kilo", kg(t.air_weight_kg)], ["Denizyolu Kilo", kg(t.sea_weight_kg)],
          ["Karayolu Kilo", kg(t.land_weight_kg)]].map(([l, v]) => (
          <div key={l} className="brut p-4" data-testid={`report-metric-${l}`}>
            <p className="overline">{l}</p>
            <p className="tnum mt-2 font-mono text-lg font-semibold">{v}</p>
          </div>
        ))}
      </div>

      <div className="brut p-4 sm:p-6" data-testid="report-mode-chart">
        <p className="overline">Taşıma Moduna Göre Gelir / Kâr</p>
        <div className="mt-4 h-72 w-full min-w-[280px]">
          {ready && (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_mode.map((m) => ({ ...m, mode: MODE_LABELS[m.mode] || m.mode }))}>
              <CartesianGrid stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="mode" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="sale" fill="#002FA7" name="Gelir" />
              <Bar dataKey="profit" fill="#34C759" name="Kâr" />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="brut overflow-x-auto" data-testid="report-mode-table">
        <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Taşıma Moduna Göre Taşınan Miktar</p></div>
        <table className="dense w-full">
          <thead><tr><th>Mod</th><th className="!text-right">Dosya</th><th className="!text-right">Kilo</th>
            <th className="!text-right">TEU</th><th className="!text-right">Araç Adedi</th>
            <th className="!text-right">Gelir</th><th className="!text-right">Kâr</th></tr></thead>
          <tbody>
            {data.by_mode.map((m, i) => (
              <tr key={m.mode} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`report-mode-row-${m.mode}`}>
                <td className="font-semibold">{MODE_LABELS[m.mode] || m.mode}</td>
                <td className="tnum text-right font-mono">{m.count}</td>
                <td className="tnum text-right font-mono">{kg(m.weight_kg)}</td>
                <td className="tnum text-right font-mono">{m.mode === "deniz" ? num(m.teu) : "-"}</td>
                <td className="tnum text-right font-mono">{m.mode === "kara" ? num(m.vehicle_count) : "-"}</td>
                <td className="tnum text-right font-mono">{money(m.sale)}</td>
                <td className="tnum text-right font-mono">{money(m.profit)}</td>
              </tr>
            ))}
            {data.by_mode.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-zinc-500">Veri yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="brut overflow-x-auto" data-testid="report-equipment-table">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-950 px-4 py-3">
          <p className="overline">Taşıma Birimine Göre Tonaj Raporu</p>
          <span className="tnum font-mono text-xs text-zinc-500">Toplam {kg(t.weight_kg)}</span>
        </div>
        <table className="dense w-full">
          <thead><tr><th>Taşıma Birimi</th><th className="!text-right">Dosya</th><th className="!text-right">Toplam Ağırlık</th>
            <th className="!text-right">TEU</th><th className="!text-right">Araç</th>
            <th className="!text-right">Ortalama Ağırlık</th><th className="!text-right">Gelir</th><th className="!text-right">Kâr</th></tr></thead>
          <tbody>
            {data.by_equipment?.map((e, i) => (
              <tr key={e.equipment} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`report-equipment-row-${e.equipment}`}>
                <td className="font-mono font-semibold">{e.equipment}</td>
                <td className="tnum text-right font-mono">{e.count}</td>
                <td className="tnum text-right font-mono">{kg(e.weight_kg)}</td>
                <td className="tnum text-right font-mono">{num(e.teu)}</td>
                <td className="tnum text-right font-mono">{num(e.vehicle_count)}</td>
                <td className="tnum text-right font-mono">{kg(e.count ? e.weight_kg / e.count : 0)}</td>
                <td className="tnum text-right font-mono">{money(e.sale)}</td>
                <td className="tnum text-right font-mono">{money(e.profit)}</td>
              </tr>
            ))}
            {(!data.by_equipment || data.by_equipment.length === 0) && (
              <tr><td colSpan={8} className="py-6 text-center text-zinc-500">Veri yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="brut overflow-x-auto" data-testid="report-status-table">
          <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Duruma Göre Operasyon</p></div>
          <table className="dense w-full">
            <thead><tr><th>Durum</th><th className="!text-right">Adet</th></tr></thead>
            <tbody>
              {data.by_status.map((s, i) => (
                <tr key={s.status} className={i % 2 ? "bg-zinc-50" : ""}>
                  <td>{STATUS_LABELS[s.status] || s.status}</td>
                  <td className="tnum text-right font-mono">{s.count}</td>
                </tr>
              ))}
              {data.by_status.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-zinc-500">Veri yok.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="brut overflow-x-auto" data-testid="report-supplier-table">
          <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Tedarikçi Bazında Taşınan Miktar & Maliyet</p></div>
          <table className="dense w-full">
            <thead><tr><th>Tedarikçi</th><th className="!text-right">Dosya</th><th className="!text-right">Kilo</th>
              <th className="!text-right">TEU</th><th className="!text-right">Araç</th>
              <th className="!text-right">Maliyet</th><th className="!text-right">Gecikme</th></tr></thead>
            <tbody>
              {data.by_supplier.map((s, i) => (
                <tr key={s.supplier_name} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`report-supplier-row-${s.supplier_name}`}>
                  <td className="font-semibold">{s.supplier_name}</td>
                  <td className="tnum text-right font-mono">{s.operation_count ?? s.operations}</td>
                  <td className="tnum text-right font-mono">{kg(s.weight_kg)}</td>
                  <td className="tnum text-right font-mono">{num(s.teu)}</td>
                  <td className="tnum text-right font-mono">{num(s.vehicle_count)}</td>
                  <td className="tnum text-right font-mono">{money(s.cost_try)}</td>
                  <td className="tnum text-right font-mono">{s.delay_hours} sa</td>
                </tr>
              ))}
              {data.by_supplier.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-zinc-500">Veri yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
